import React from "react";
import {
  Alert,
  Autocomplete,
  Checkbox,
  FormControl,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

import { isValidArray } from "utils/array";
import ExpandableTags from "components/common/expandable-chips/ExpandableTags";

// Chips shown before the rest collapse into "+N"
const VISIBLE_RECIPIENTS = 6;

/**
 * Email recipients of an alert notification as removable chips. The first few show, the rest
 * collapse into a "+N" chip that expands them ("Show Less" collapses again). Clicking the field
 * lists the configured recipients with checkboxes, so a removed one can be added back.
 * With no recipients configured it shows a note instead.
 */
const AlertEmailRecipients = ({
  recipients,
  selectedRecipients = [],
  isDisabled,
  onChange,
}) => {
  if (!isValidArray(recipients)) {
    return (
      <Alert severity="info" className="mb-2">
        No email recipients are set up for this alert. Add them in Notification
        Preferences to send it by email.
      </Alert>
    );
  }

  const hasNoneSelected = selectedRecipients.length === 0;
  return (
    <FormControl fullWidth className="mb-2">
      <Autocomplete
        multiple
        disableCloseOnSelect
        forcePopupIcon={false}
        options={recipients}
        value={selectedRecipients}
        disabled={isDisabled}
        onChange={(e, value) =>
          // Keep the configured order, whatever order they were picked in
          onChange(recipients.filter((recipient) => value.includes(recipient)))
        }
        renderTags={(value, getTagProps) => (
          <ExpandableTags
            value={value}
            getTagProps={getTagProps}
            visibleCount={VISIBLE_RECIPIENTS}
            chipProps={{ deleteIcon: <CloseIcon /> }}
            itemLabel="recipients"
          />
        )}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              checked={selected}
              color="secondary"
              style={{ marginRight: 8 }}
            />
            {option}
          </li>
        )}
        sx={{
          // Keep the clear-all button visible, not only on hover
          "& .MuiAutocomplete-clearIndicator": { visibility: "visible" },
          // Clear-all at the top right, level with the first row of chips (as in the design)
          "& .MuiAutocomplete-endAdornment": { top: 10, transform: "none" },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Email Recipients"
            required
            size="small"
            variant="outlined"
            color="secondary"
            inputProps={{ ...params.inputProps, readOnly: true }}
            error={hasNoneSelected}
            helperText={
              hasNoneSelected && "Select at least one email to send this alert."
            }
          />
        )}
      />
    </FormControl>
  );
};

export default AlertEmailRecipients;
